import { useState, useEffect, useCallback } from 'react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';

// Global cache for admin status
let adminCache: { isAdmin: boolean; userId: string; timestamp: number } | null = null;
let pendingPromise: Promise<boolean> | null = null;
const CACHE_DURATION = 60 * 1000; // 60 seconds

export const useIsAdmin = () => {
  const { user } = useSecureAuthContext();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAdminStatus = useCallback(async (): Promise<boolean> => {
    if (!user?.email) {
      return false;
    }

    // Check cache first
    if (adminCache && 
        adminCache.userId === user.id && 
        (Date.now() - adminCache.timestamp) < CACHE_DURATION) {
      return adminCache.isAdmin;
    }

    // Deduplicate concurrent requests
    if (pendingPromise) {
      return pendingPromise;
    }

    pendingPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('admin_users')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();

        const result = !error && !!data;
        
        // Update cache
        adminCache = {
          isAdmin: result,
          userId: user.id,
          timestamp: Date.now()
        };
        
        return result;
      } catch {
        return false;
      } finally {
        pendingPromise = null;
      }
    })();

    return pendingPromise;
  }, [user?.id, user?.email]);

  useEffect(() => {
    if (!user?.id) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    checkAdminStatus().then(result => {
      setIsAdmin(result);
      setLoading(false);
    });
  }, [user?.id, checkAdminStatus]);

  return { isAdmin: isAdmin ?? false, loading };
};

// Utility to clear cache (useful on logout)
export const clearAdminCache = () => {
  adminCache = null;
  pendingPromise = null;
};
